import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, QuoteIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslations } from '../i18n/translations';
const testimonialAuthors = [{
  author: 'Aleksandr Katerinich',
  company: 'Code-Smith',
  companyInitial: 'C',
  companyColor: 'bg-blue-500'
}, {
  author: 'Under NDA',
  company: 'SaaS Company',
  companyInitial: 'S',
  companyColor: 'bg-emerald-500'
}, {
  author: 'Vladimir Voloshin',
  company: 'Dizair',
  companyInitial: 'D',
  companyColor: 'bg-violet-500'
}, {
  author: 'Roman Dzhulay',
  company: 'Andromo LTD',
  companyInitial: 'A',
  companyColor: 'bg-amber-500'
}, {
  author: 'Yuliya Barabash',
  company: 'SBSB Fintech Lawyers',
  companyInitial: 'S',
  companyColor: 'bg-rose-500'
}, {
  author: 'Vladimir Kuriy',
  company: 'para.agency',
  companyInitial: 'P',
  companyColor: 'bg-teal-500'
}];
export function TestimonialsSection() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const next = () => {
    setDirection(1);
    setCurrent(prev => (prev + 1) % t.testimonials.items.length);
  };
  const prev = () => {
    setDirection(-1);
    setCurrent(prev => (prev - 1 + t.testimonials.items.length) % t.testimonials.items.length);
  };
  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, []);
  const testimonial = t.testimonials.items[current];
  const author = testimonialAuthors[current];
  const slideVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 80 : -80,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -80 : 80,
      opacity: 0
    })
  };
  return <section className="relative py-20 lg:py-28 bg-white overflow-hidden" data-id="element-1217" data-mp="8fydu0eauwz1">
      <div className="absolute inset-0 pointer-events-none" data-id="element-1218" data-mp="de9jdyxkalbg">
        <div className="absolute top-0 left-1/3 w-[400px] h-[400px] rounded-full bg-primary-50/25 blur-3xl hidden sm:block" data-id="element-1219" data-mp="ickxiluw3hzh" />
        <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] rounded-full bg-cyan-50/20 blur-3xl hidden sm:block" data-id="element-1220" data-mp="3guq95hxwi5kn" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-1221" data-mp="3lqniv5ensdog">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14" data-id="element-1222" data-mp="3qq079ul6hst5">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-1223" data-mp="1k6cu4lsgvcql">
            {t.testimonials.eyebrow}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-1224" data-mp="1b67ooh6u2of2">
            {t.testimonials.title}
          </h2>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="max-w-3xl mx-auto" data-id="element-1225" data-mp="169r0xo3jqc2i">
          <div className="relative bg-gradient-to-br from-navy-50/80 via-white to-primary-50/30 rounded-3xl p-8 sm:p-12 border border-navy-100 shadow-lg shadow-navy-100/30 overflow-hidden" data-id="element-1226" data-mp="nf29m48pw05t">
            <QuoteIcon className="absolute top-6 right-8 w-32 h-32 text-primary-100/40" data-id="element-1227" data-mp="dl1tl7bwdaib" />
            <QuoteIcon className="relative z-10 w-10 h-10 text-primary-400 mb-6" data-id="element-1228" data-mp="2dn1po5r2awbx" />

            <div className="relative z-10 h-[340px] sm:h-[300px] lg:h-[250px]" data-id="element-1229" data-mp="2ijiez39zzgql">
              <AnimatePresence mode="wait" custom={direction} data-id="element-1230" data-mp="23u3yzcbx2128">
                <motion.div key={current} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
                duration: 0.35
              }} className="absolute inset-0 flex flex-col" data-id="element-1231" data-mp="28tyslnn2t4qz">
                  <blockquote className="text-base sm:text-lg lg:text-xl text-navy-700 leading-relaxed font-medium flex-1 overflow-hidden" data-id="element-1232" data-mp="1puul4jb6nj1f">
                    «{testimonial.quote}»
                  </blockquote>
                  <div className="mt-6 flex items-center gap-4 flex-shrink-0" data-id="element-1233" data-mp="3k9nh4jfzabtw">
                    <div className={`w-12 h-12 rounded-full ${author.companyColor} flex items-center justify-center text-white font-bold text-sm shadow-lg`} data-id="element-1234" data-mp="18lsbgl7zirnf">
                      {author.companyInitial}
                    </div>
                    <div data-id="element-1235" data-mp="1idoco55daxvv">
                      <div className="font-bold text-navy-900" data-id="element-1236" data-mp="1dlen4q85zzu4">
                        {author.author}
                      </div>
                      <div className="text-sm text-navy-500" data-id="element-1237" data-mp="1rb8ebf756w70">
                        {testimonial.role}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          <div className="flex items-center justify-between mt-6 gap-3 px-2" data-id="element-1238" data-mp="1wb4m7piyn7ru">
            <div className="flex gap-1.5 sm:gap-2 flex-wrap" data-id="element-1239" data-mp="6wonvji8b16l">
              {t.testimonials.items.map((_, i) => <button key={i} onClick={() => {
              setDirection(i > current ? 1 : -1);
              setCurrent(i);
            }} className={`h-2 sm:h-2.5 rounded-full transition-all duration-300 ${i === current ? 'bg-primary-500 w-6 sm:w-8 shadow-sm shadow-primary-500/30' : 'bg-navy-300/60 w-2 sm:w-2.5 hover:bg-navy-400'}`} aria-label={`Testimonial ${i + 1}`} data-id="element-1240" data-mp="1y4wh3kv8x6u" data-mpi={i} />)}
            </div>
            <div className="flex gap-2 flex-shrink-0" data-id="element-1241" data-mp="2kxnteicc4vbs">
              <button onClick={prev} className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-white border border-navy-200 flex items-center justify-center text-navy-500 hover:bg-navy-50 hover:border-navy-300 transition-all shadow-sm" data-id="element-1242" data-mp="2kww6dvwt05x4">
                <ChevronLeftIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-1243" data-mp="2ypc2sdv795p2" />
              </button>
              <button onClick={next} className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-white border border-navy-200 flex items-center justify-center text-navy-500 hover:bg-navy-50 hover:border-navy-300 transition-all shadow-sm" data-id="element-1244" data-mp="33qrqyesobsbo">
                <ChevronRightIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-1245" data-mp="3ds3shhyz4xb3" />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}